/**
 * Memorial API Service
 * Handles shared persistence for candles and stones via Cloudflare Workers + KV
 */

// API base URL - set via environment variable or defaults to relative path
// For production, set REACT_APP_API_URL to your Cloudflare Worker URL
const API_BASE = process.env.REACT_APP_API_URL || '';

// Cache for shared counts
let candleCountsCache = null;
let stoneCountsCache = null;
let lastCandleFetch = 0;
let lastStoneFetch = 0;
const CACHE_TTL = 30000; // 30 seconds

/**
 * Fetch all candle counts from the server
 */
export async function fetchCandleCounts() {
  const now = Date.now();
  if (candleCountsCache && (now - lastCandleFetch) < CACHE_TTL) {
    return candleCountsCache;
  }

  try {
    const response = await fetch(`${API_BASE}/candles`);
    if (!response.ok) {
      throw new Error('Failed to fetch candle counts');
    }
    candleCountsCache = await response.json();
    lastCandleFetch = now;
    return candleCountsCache;
  } catch (error) {
    console.error('Error fetching candle counts:', error);
    return candleCountsCache || {};
  }
}

/**
 * Light a candle for a memorial (server-side)
 */
export async function lightCandle(memorialId) {
  try {
    const response = await fetch(`${API_BASE}/candles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ memorialId: String(memorialId), action: 'light' }),
    });

    if (!response.ok) {
      throw new Error('Failed to light candle');
    }

    const result = await response.json();

    // Update cache
    if (candleCountsCache) {
      candleCountsCache[memorialId] = result.count;
    }

    return result;
  } catch (error) {
    console.error('Error lighting candle:', error);
    throw error;
  }
}

/**
 * Get candle count for a specific memorial
 */
export async function getCandleCount(memorialId) {
  const counts = await fetchCandleCounts();
  return parseInt(counts[memorialId] || 0, 10);
}

/**
 * Fetch all stone counts from the server
 */
export async function fetchStoneCounts() {
  const now = Date.now();
  if (stoneCountsCache && (now - lastStoneFetch) < CACHE_TTL) {
    return stoneCountsCache;
  }

  try {
    const response = await fetch(`${API_BASE}/stones`);
    if (!response.ok) {
      throw new Error('Failed to fetch stone counts');
    }
    stoneCountsCache = await response.json();
    lastStoneFetch = now;
    return stoneCountsCache;
  } catch (error) {
    console.error('Error fetching stone counts:', error);
    return stoneCountsCache || {};
  }
}

/**
 * Place a stone for a memorial (server-side)
 */
export async function placeStone(memorialId) {
  try {
    const response = await fetch(`${API_BASE}/stones`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ memorialId: String(memorialId), action: 'place' }),
    });

    if (!response.ok) {
      throw new Error('Failed to place stone');
    }

    const result = await response.json();

    // Update cache
    if (stoneCountsCache) {
      stoneCountsCache[memorialId] = result.count;
    }

    return result;
  } catch (error) {
    console.error('Error placing stone:', error);
    throw error;
  }
}

/**
 * Get stone count for a specific memorial
 */
export async function getStoneCount(memorialId) {
  const counts = await fetchStoneCounts();
  return parseInt(counts[memorialId] || 0, 10);
}

/**
 * Prefetch both candle and stone counts
 */
export async function prefetchCounts() {
  await Promise.all([fetchCandleCounts(), fetchStoneCounts()]);
}

/**
 * Clear the cache (useful after user actions)
 */
export function clearCache() {
  candleCountsCache = null;
  stoneCountsCache = null;
  lastCandleFetch = 0;
  lastStoneFetch = 0;
}
